export interface AdviIntervalDTO {
  value: number;
  unit: 'days' | 'weeks' | 'months';
}

export type ChartType = 'bar' | 'pie';

export type ReportTypeId =
  | 'event_report'
  | 'module_participation'
  | 'participation_snapshot'
  | 'user_progress_simple'
  | 'module_matrix_simple'
  | 'lagging_only'
  | 'merit_summary'
  | 'minimal_onepager';

export type ScheduleMode = 'before_expiry' | 'calendar';

export type PathVersionMode = 'grouped' | 'separated';

export interface SnapshotDateConfig {
  mode: 'year' | 'range';
  year?: number;
  date_from?: string;
  date_to?: string;
}

export interface CalendarInterval {
  value: number;
  unit: 'days' | 'weeks' | 'months';
}

export interface ReportDeliveryConfig {
  advisering_enabled: boolean;
  schedule_mode: ScheduleMode;
  recipient_user_ids: number[];
  advisory_intervals: AdviIntervalDTO[];
  calendar_interval?: CalendarInterval;
  date: SnapshotDateConfig;
}

export interface ReportSectionDefinition {
  id: string;
  label: string;
  default: boolean;
}

export interface ReportDimensionDefinition {
  id: string;
  label: string;
  default: boolean;
}

/** Select-type setting of a report type (e.g. sorting). */
export interface ReportOptionDefinition {
  id: string;
  label: string;
  default: string;
  choices: { value: string; label: string }[];
}

export interface ReportTypeDefinition {
  id: ReportTypeId;
  name: string;
  description: string;
  category: 'simple' | 'detailed' | 'minimal';
  default_schedule_mode: ScheduleMode;
  supports_advisering: boolean;
  sections: ReportSectionDefinition[];
  dimensions: ReportDimensionDefinition[];
  charts: { id: ChartType; label: string; default: boolean }[];
  options?: ReportOptionDefinition[];
}

export interface ReportConfig {
  report_type: ReportTypeId;
  sections: Record<string, boolean>;
  path_ids: number[];
  module_ids: number[];
  /** module_id → display name (from wizard module list) */
  module_labels?: Record<number, string>;
  location_guids: string[];
  user_ids: number[];
  group_ids: number[];
  path_version_mode: PathVersionMode;
  delivery: ReportDeliveryConfig;
  dimensions: Record<string, boolean>;
  charts: { types: ChartType[] };
  /** Values of the type's select options (see ReportTypeDefinition.options). */
  options?: Record<string, string>;
}

/** @deprecated Use ReportConfig — kept for backward compatibility */
export interface SnapshotDimensions {
  per_user: boolean;
  per_station: boolean;
  per_organization: boolean;
}

/** @deprecated Use ReportConfig */
export interface ParticipationSnapshotReportConfig {
  location_guids: string[];
  user_ids: number[];
  module_ids: number[];
  date: SnapshotDateConfig;
  dimensions: SnapshotDimensions;
  charts: { types: ChartType[] };
}

export interface AdviDTO {
  advi_id?: number;
  organizations_id: number;
  name: string;
  description?: string;
  path_ids: number[];
  report_type: ReportTypeId | string;
  report_config?: ReportConfig | ParticipationSnapshotReportConfig | any;
  recipient_user_ids: number[];
  advisory_intervals: AdviIntervalDTO[];
  is_active: boolean;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
  last_sent_at?: string;
}

export function createDefaultReportConfig(type: ReportTypeId = 'event_report'): ReportConfig {
  const year = new Date().getFullYear();
  return {
    report_type: type,
    sections: {},
    path_ids: [],
    module_ids: [],
    location_guids: [],
    user_ids: [],
    group_ids: [],
    path_version_mode: 'grouped',
    delivery: {
      advisering_enabled: false,
      schedule_mode: type === 'event_report' || type === 'lagging_only' ? 'before_expiry' : 'calendar',
      recipient_user_ids: [],
      advisory_intervals: [{ value: 1, unit: 'months' }],
      calendar_interval: { value: 1, unit: 'months' },
      date: { mode: 'year', year, date_from: `${year}-01-01`, date_to: `${year}-12-31` },
    },
    dimensions: {},
    charts: { types: ['bar'] },
    options: {},
  };
}

export function migrateLegacyAdviConfig(advi: AdviDTO): ReportConfig {
  const base = createDefaultReportConfig((advi.report_type as ReportTypeId) || 'event_report');
  const legacy = advi.report_config || {};
  const rc = legacy as Partial<ReportConfig>;

  // Legacy participation_snapshot (ingen report_type / sections)
  if (!rc.report_type && !rc.sections && (legacy.location_guids || legacy.date || legacy.dimensions)) {
    return {
      ...base,
      report_type: (advi.report_type as ReportTypeId) || 'participation_snapshot',
      path_ids: advi.path_ids || [],
      module_ids: legacy.module_ids || [],
      location_guids: legacy.location_guids || [],
      user_ids: legacy.user_ids || [],
      group_ids: rc.group_ids ?? [],
      delivery: {
        ...base.delivery,
        advisering_enabled: false,
        date: legacy.date || base.delivery.date,
      },
      dimensions: legacy.dimensions || {},
      charts: legacy.charts || { types: ['bar'] },
    };
  }

  // Unified report_config (module_participation, event_report, osv.)
  if (rc.report_type || rc.sections || rc.delivery) {
    const pathIds = rc.path_ids !== undefined
      ? rc.path_ids
      : (advi.path_ids || []);
    return {
      ...base,
      ...rc,
      report_type: (rc.report_type ?? advi.report_type ?? base.report_type) as ReportTypeId,
      path_ids: pathIds,
      module_ids: rc.module_ids ?? [],
      location_guids: rc.location_guids ?? [],
      user_ids: rc.user_ids ?? [],
      group_ids: rc.group_ids ?? [],
      sections: { ...base.sections, ...(rc.sections || {}) },
      dimensions: { ...base.dimensions, ...(rc.dimensions || {}) },
      charts: rc.charts?.types?.length ? rc.charts : base.charts,
      options: { ...base.options, ...(rc.options || {}) },
      delivery: {
        ...base.delivery,
        ...(rc.delivery || {}),
        advisering_enabled: rc.delivery?.advisering_enabled ?? ((advi.recipient_user_ids?.length ?? 0) > 0),
        recipient_user_ids: rc.delivery?.recipient_user_ids ?? advi.recipient_user_ids ?? [],
        advisory_intervals: rc.delivery?.advisory_intervals?.length
          ? rc.delivery.advisory_intervals
          : (advi.advisory_intervals?.length ? advi.advisory_intervals : base.delivery.advisory_intervals),
        calendar_interval:
          rc.delivery?.calendar_interval ?? base.delivery.calendar_interval,
        date: rc.delivery?.date ?? legacy.date ?? base.delivery.date,
      },
    };
  }

  return {
    ...base,
    report_type: (advi.report_type as ReportTypeId) || 'event_report',
    path_ids: advi.path_ids || [],
    module_ids: legacy.module_ids ?? [],
    location_guids: legacy.location_guids ?? [],
    user_ids: legacy.user_ids ?? [],
    group_ids: rc.group_ids ?? [],
    delivery: {
      ...base.delivery,
      advisering_enabled: (advi.recipient_user_ids?.length ?? 0) > 0,
      recipient_user_ids: advi.recipient_user_ids || [],
      advisory_intervals: advi.advisory_intervals?.length
        ? advi.advisory_intervals
        : [{ value: 1, unit: 'months' }],
    },
  };
}

export function applyTypeDefaults(config: ReportConfig, typeDef: ReportTypeDefinition): ReportConfig {
  const sections: Record<string, boolean> = { ...config.sections };
  typeDef.sections.forEach(s => {
    if (sections[s.id] === undefined) sections[s.id] = s.default;
  });
  const dimensions: Record<string, boolean> = { ...config.dimensions };
  typeDef.dimensions.forEach(d => {
    if (dimensions[d.id] === undefined) dimensions[d.id] = d.default;
  });
  const options: Record<string, string> = { ...(config.options ?? {}) };
  (typeDef.options ?? []).forEach(o => {
    if (options[o.id] === undefined) options[o.id] = o.default;
  });
  const chartTypes = config.charts?.types?.length
    ? config.charts.types
    : typeDef.charts.filter(c => c.default).map(c => c.id);
  return {
    ...config,
    report_type: typeDef.id,
    sections,
    dimensions,
    options,
    charts: { types: chartTypes },
    delivery: {
      ...config.delivery,
      schedule_mode: config.delivery.schedule_mode || typeDef.default_schedule_mode,
    },
  };
}
